import { LOG_LEVELS } from '@crm/contracts';
import { Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CircleAlert,
  CircleCheck,
  Inbox,
  Layers,
  Mail,
  RotateCcw,
  Gauge,
  ScrollText,
  ShieldAlert,
  Timer,
  Trash2,
  type LucideIcon,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Gate } from '../components/shell/gate.tsx';
import { Badge } from '../components/ui/badge.tsx';
import { Button } from '../components/ui/button.tsx';
import { Card } from '../components/ui/card.tsx';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog.tsx';
import { PageHeader } from '../components/ui/page-header.tsx';
import { describeError } from '../lib/api.ts';
import {
  api,
  type BreakerView,
  type DeadLetterView,
  type LogLevelView,
  type RuntimeLogLevel,
  type PerformanceView,
  type SystemHealthView,
} from '../lib/client.ts';
import { cn } from '../lib/utils.ts';
import { nativeSelectClass } from '../components/ui/input.tsx';

const QUERY = ['system-health'] as const;
const DEAD = ['dead-letters'] as const;
/** "An open breaker is visible within seconds": the reporters publish on change, the page polls. */
const POLL_MS = 5_000;
/** Instances publish performance every 30s; polling faster would only re-read the same numbers. */
const PERF_POLL_MS = 30_000;
/** The same lines the CloudWatch alarms draw (infra/modules/monitoring), so screen and pager agree. */
const P95_ALARM_MS = 1_500;
const ERROR_RATE_ALARM = 0.05;
const LOOP_DELAY_WARN_MS = 100;

const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : '—');

/** "3m ago" for recent times, the date beyond a day. */
function ago(iso: string | null, now = Date.now()): string {
  if (!iso) return 'never';
  const s = Math.max(0, Math.round((now - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86_400) return `${Math.round(s / 3600)}h ago`;
  return new Date(iso).toLocaleDateString();
}

function every(seconds: number): string {
  if (seconds < 60) return `every ${seconds}s`;
  if (seconds < 3600) return `every ${Math.round(seconds / 60)} min`;
  if (seconds < 86_400) return `every ${Math.round(seconds / 3600)} h`;
  return 'daily';
}

const BREAKER_STATE = {
  closed: { label: 'Closed', variant: 'success' },
  'half-open': { label: 'Probing', variant: 'warning' },
  open: { label: 'Open', variant: 'danger' },
} as const;

const JOB_HEALTH = {
  ok: { label: 'OK', variant: 'success' },
  stale: { label: 'Stale', variant: 'danger' },
  failing: { label: 'Failing', variant: 'danger' },
  never_ran: { label: 'Never ran', variant: 'warning' },
  off: { label: 'Switched off', variant: 'secondary' },
} as const satisfies Record<
  string,
  { label: string; variant: 'success' | 'warning' | 'danger' | 'secondary' }
>;
/** An optional job (fed from outside, TS-1892) with no heartbeat: not set up here, not an outage. */
const NOT_SET_UP = { label: 'Not set up', variant: 'secondary' } as const;

/**
 * Settings → System Health (TS-1039): the first place an admin looks when something is off.
 * Circuit breakers for every external dependency and this organisation's webhook targets (with
 * what the product does while one is open), the job queues and this organisation's dead-lettered
 * jobs (retry or discard), the worker's scheduled loops, mail and message sync, and the runtime
 * log level per service. It polls every few seconds.
 */
export function SystemHealthPage() {
  const health = useQuery({
    queryKey: QUERY,
    queryFn: api.system.health,
    refetchInterval: POLL_MS,
  });
  const data = health.data;
  return (
    <Gate permission="organisation.manage">
      <PageHeader
        title="System Health"
        description="Breakers, queues, failed jobs, scheduled jobs, sync and log levels, refreshed every few seconds."
        actions={data && <Overall data={data} />}
      />
      {health.isPending && <p className="text-muted-foreground text-sm">Loading…</p>}
      {health.isError && (
        <p
          className="bg-state-danger-bg text-state-danger-fg border-state-danger-border rounded-xl border px-4 py-3 text-sm"
          role="alert"
        >
          {describeError(health.error)}
        </p>
      )}
      {data && (
        <div className="flex flex-col gap-8">
          <Breakers breakers={data.breakers} />
          <PerformanceSection />
          <Queues data={data} />
          <Jobs jobs={data.jobs} />
          <Sync sync={data.sync} />
          <LogLevels levels={data.logLevels} />
        </div>
      )}
    </Gate>
  );
}

function Overall({ data }: { data: SystemHealthView }) {
  return (
    <span className="flex flex-wrap items-center gap-2 text-xs" data-testid="overall">
      {data.healthy ? (
        <Badge variant="success">
          <CircleCheck className="size-3.5" aria-hidden /> All healthy
        </Badge>
      ) : (
        <Badge variant="danger">
          <CircleAlert className="size-3.5" aria-hidden /> Needs attention
        </Badge>
      )}
      <span className="text-muted-foreground">
        Checked {new Date(data.checkedAt).toLocaleTimeString()}
      </span>
    </span>
  );
}

function Section({
  icon: Icon,
  title,
  description,
  children,
  testId,
}: {
  icon: LucideIcon;
  title: string;
  description: ReactNode;
  children: ReactNode;
  testId: string;
}) {
  return (
    <section data-testid={testId} className="min-w-0">
      <div className="mb-3 flex items-start gap-3">
        <span className="bg-brand-soft text-brand-soft-foreground grid size-9 shrink-0 place-items-center rounded-xl">
          <Icon className="size-[17px]" aria-hidden />
        </span>
        <div className="min-w-0">
          <h2 className="text-base font-semibold">{title}</h2>
          <p className="text-muted-foreground text-sm">{description}</p>
        </div>
      </div>
      {children}
    </section>
  );
}

function Breakers({ breakers }: { breakers: BreakerView[] }) {
  const open = breakers.filter((b) => b.state !== 'closed').length;
  return (
    <Section
      icon={ShieldAlert}
      title="Circuit breakers"
      testId="breakers"
      description={
        open
          ? `${open} not closed. While a breaker is open, calls to it fail at once instead of waiting; each card says what that means for users.`
          : 'Every external dependency, and each of your webhook targets and automation receivers that has been called. Counts cover the last ~10 seconds.'
      }
    >
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {breakers.map((b) => (
          <BreakerCard key={b.name} b={b} />
        ))}
      </div>
    </Section>
  );
}

function BreakerCard({ b }: { b: BreakerView }) {
  const state = BREAKER_STATE[b.state];
  const s = b.stats;
  const calls = s.successes + s.failures + s.timeouts + s.rejects;
  return (
    <Card
      className={cn(
        'flex min-w-0 flex-col gap-3 p-4',
        b.state === 'open' && 'border-state-danger-border',
        b.state === 'half-open' && 'border-state-warning-border',
      )}
      data-testid="breaker"
      data-state={b.state}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold break-all">{b.label}</p>
          <p className="text-muted-foreground font-mono text-[11px] break-all">
            {b.kind === 'webhook'
              ? 'webhook target'
              : b.kind === 'automation'
                ? 'automation receiver'
                : b.name}
          </p>
        </div>
        <Badge variant={state.variant} className="shrink-0">
          {state.label}
        </Badge>
      </div>
      <p className="text-muted-foreground text-xs">
        {b.reportedAt === null
          ? 'No calls since the last deploy.'
          : calls === 0
            ? `Idle · ${b.state} since ${ago(b.since)}`
            : `${s.successes} ok · ${s.failures} failed · ${s.timeouts} timed out · ${s.rejects} not attempted`}
        {b.state !== 'closed' && b.since && ` · ${state.label.toLowerCase()} since ${ago(b.since)}`}
      </p>
      <p
        className={cn(
          'rounded-lg px-3 py-2 text-xs leading-relaxed',
          b.state === 'open'
            ? 'bg-state-danger-bg text-state-danger-fg'
            : 'bg-muted text-muted-foreground',
        )}
      >
        <span className="font-semibold">While open: </span>
        {b.fallback}
      </p>
      {(b.instances.length > 1 || b.kind === 'webhook') && (
        <p className="text-muted-foreground text-[11px]">
          {b.instances.length > 1 &&
            `${b.instances.filter((i) => i.state !== 'closed').length} of ${b.instances.length} instances not closed. `}
          {b.kind === 'webhook' && (
            <Link to="/settings/webhooks" className="text-brand underline-offset-2 hover:underline">
              Open webhooks
            </Link>
          )}
        </p>
      )}
    </Card>
  );
}

function Queues({ data }: { data: SystemHealthView }) {
  return (
    <Section
      icon={Layers}
      title="Job queues"
      testId="queues"
      description="Background work waiting, running and failed. A job that fails its last retry is dead-lettered below, never dropped."
    >
      {data.queues === null ? (
        <p className="bg-muted text-muted-foreground rounded-xl px-4 py-3 text-sm">
          The queues cannot be read: the API has no Redis (REDIS_URL), or Redis did not answer.
        </p>
      ) : (
        <>
          <div className="bg-card border-border overflow-x-auto rounded-xl border">
            <table className="w-full min-w-[560px] text-left text-sm">
              <thead className="text-muted-foreground bg-muted text-xs whitespace-nowrap">
                <tr>
                  <th className="px-3 py-2 font-semibold">Queue</th>
                  <th className="px-3 py-2 text-right font-semibold">Waiting</th>
                  <th className="px-3 py-2 text-right font-semibold">Running</th>
                  <th className="px-3 py-2 text-right font-semibold">Delayed</th>
                  <th className="px-3 py-2 text-right font-semibold">Failed</th>
                  <th className="px-3 py-2 text-right font-semibold">Done</th>
                </tr>
              </thead>
              <tbody>
                {data.queues.map((q) => (
                  <tr key={q.name} className="border-border border-t" data-testid="queue">
                    <td className="px-3 py-2 font-mono text-xs">{q.name}</td>
                    <Num n={q.waiting} />
                    <Num n={q.active} />
                    <Num n={q.delayed} />
                    <Num n={q.failed} tone="danger" />
                    <Num n={q.completed} />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <DeadLetters count={data.deadLettered ?? 0} />
        </>
      )}
    </Section>
  );
}

function Num({ n, tone }: { n: number; tone?: 'danger' }) {
  return (
    <td
      className={cn(
        'px-3 py-2 text-right tabular-nums',
        n === 0 ? 'text-muted-foreground' : tone === 'danger' && 'text-danger font-semibold',
      )}
    >
      {n}
    </td>
  );
}

function DeadLetters({ count }: { count: number }) {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: [...DEAD, count], queryFn: api.system.deadLetters });
  const [discarding, setDiscarding] = useState<DeadLetterView | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: DEAD });
    await qc.invalidateQueries({ queryKey: QUERY });
  };
  const retry = useMutation({
    mutationFn: (j: DeadLetterView) => api.system.retry(j.id),
    onSuccess: async (r) => {
      setError(null);
      setNotice(`Back on ${r.queue} with a fresh set of retries.`);
      await refresh();
    },
    onError: (err) => setError(describeError(err)),
  });
  const discard = useMutation({
    mutationFn: (j: DeadLetterView) => api.system.discard(j.id),
    onSuccess: async () => {
      setDiscarding(null);
      setError(null);
      setNotice('Discarded.');
      await refresh();
    },
    onError: (err) => setError(describeError(err)),
  });
  const rows = list.data ?? [];
  return (
    <div className="mt-4" data-testid="dead-letters">
      <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold">
        <Inbox className="size-4" aria-hidden /> Dead-lettered jobs
        <Badge variant={rows.length ? 'danger' : 'secondary'}>{rows.length}</Badge>
      </h3>
      {notice && (
        <p className="text-state-success-fg mb-2 text-sm" role="status">
          {notice}
        </p>
      )}
      {error && (
        <p className="text-danger mb-2 text-sm" role="alert">
          {error}
        </p>
      )}
      {list.isError && (
        <p className="text-danger text-sm" role="alert">
          {describeError(list.error)}
        </p>
      )}
      {list.data && rows.length === 0 && (
        <p className="text-muted-foreground bg-muted rounded-xl px-4 py-3 text-sm">
          None. Every job either succeeded or is still being retried.
        </p>
      )}
      {rows.length > 0 && (
        <div className="bg-card border-border overflow-x-auto rounded-xl border">
          <table className="w-full min-w-[720px] text-left text-sm">
            <thead className="text-muted-foreground bg-muted text-xs whitespace-nowrap">
              <tr>
                <th className="px-3 py-2 font-semibold">Failed</th>
                <th className="px-3 py-2 font-semibold">Job</th>
                <th className="px-3 py-2 font-semibold">Why</th>
                <th className="px-3 py-2 font-semibold">Attempts</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {rows.map((j) => (
                <DeadLetterRow
                  key={j.id}
                  j={j}
                  expanded={open === j.id}
                  onToggle={() => setOpen(open === j.id ? null : j.id)}
                  onRetry={() => retry.mutate(j)}
                  onDiscard={() => setDiscarding(j)}
                  busy={retry.isPending || discard.isPending}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Dialog open={discarding !== null} onOpenChange={(o) => !o && setDiscarding(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Discard this job?</DialogTitle>
            <DialogDescription>
              {discarding?.name} on {discarding?.queue} will not run again. The discard is in the
              audit log.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDiscarding(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => discarding && discard.mutate(discarding)}
              disabled={discard.isPending}
              data-testid="confirm-discard"
            >
              Discard
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DeadLetterRow({
  j,
  expanded,
  onToggle,
  onRetry,
  onDiscard,
  busy,
}: {
  j: DeadLetterView;
  expanded: boolean;
  onToggle: () => void;
  onRetry: () => void;
  onDiscard: () => void;
  busy: boolean;
}) {
  return (
    <>
      <tr className="border-border border-t align-top" data-testid="dead-letter">
        <td className="px-3 py-2 text-xs whitespace-nowrap">{when(j.failedAt)}</td>
        <td className="px-3 py-2">
          <p className="font-mono text-xs">{j.name}</p>
          <p className="text-muted-foreground font-mono text-[11px]">{j.queue}</p>
        </td>
        <td className="text-danger max-w-[320px] px-3 py-2 text-xs break-words">
          {j.failedReason}
        </td>
        <td className="px-3 py-2 text-xs tabular-nums">{j.attemptsMade}</td>
        <td className="px-3 py-2">
          <span className="flex justify-end gap-1">
            <Button size="sm" variant="ghost" onClick={onToggle} aria-expanded={expanded}>
              Data
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={onRetry}
              disabled={busy}
              aria-label={`Retry ${j.name}`}
            >
              <RotateCcw /> Retry
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="text-danger hover:text-danger"
              onClick={onDiscard}
              disabled={busy}
              aria-label={`Discard ${j.name}`}
            >
              <Trash2 />
            </Button>
          </span>
        </td>
      </tr>
      {expanded && (
        <tr className="bg-muted/40">
          <td colSpan={5} className="px-3 py-2">
            <pre className="max-h-64 overflow-auto font-mono text-[11px] whitespace-pre-wrap break-all">
              {JSON.stringify(j.data, null, 2)}
            </pre>
          </td>
        </tr>
      )}
    </>
  );
}

/** Which message feed a job is, for its set-aside rows. */
const feedOf = (job: string): 'texts' | 'emails' | null =>
  job === 'message-feed-texts' ? 'texts' : job === 'message-feed-emails' ? 'emails' : null;

function Jobs({ jobs }: { jobs: SystemHealthView['jobs'] }) {
  const [reviewing, setReviewing] = useState<'texts' | 'emails' | null>(null);
  return (
    <Section
      icon={Timer}
      title="Scheduled jobs"
      testId="jobs"
      description="The worker's loops. Each records every run, including runs with nothing to do, so a stopped loop shows as stale."
    >
      <div className="bg-card border-border overflow-x-auto rounded-xl border">
        <table className="w-full min-w-[720px] text-left text-sm">
          <thead className="text-muted-foreground bg-muted text-xs whitespace-nowrap">
            <tr>
              <th className="px-3 py-2 font-semibold">Job</th>
              <th className="px-3 py-2 font-semibold">Health</th>
              <th className="px-3 py-2 font-semibold">Last run</th>
              <th className="px-3 py-2 font-semibold">Last work</th>
              <th className="px-3 py-2 font-semibold">Result</th>
            </tr>
          </thead>
          <tbody>
            {jobs.map((j) => {
              const h = j.optional && j.health === 'never_ran' ? NOT_SET_UP : JOB_HEALTH[j.health];
              return (
                <tr key={j.job} className="border-border border-t align-top" data-testid="job">
                  <td className="px-3 py-2">
                    <p className="font-mono text-xs">{j.job}</p>
                    <p className="text-muted-foreground text-xs">
                      {j.description} · {every(j.expectedEverySeconds)}
                    </p>
                  </td>
                  <td className="px-3 py-2">
                    <Badge variant={h.variant}>{h.label}</Badge>
                  </td>
                  <td className="px-3 py-2 text-xs whitespace-nowrap" title={when(j.lastRunAt)}>
                    {ago(j.lastRunAt)}
                  </td>
                  <td className="px-3 py-2 text-xs whitespace-nowrap" title={when(j.lastWorkAt)}>
                    {ago(j.lastWorkAt)}
                  </td>
                  <td className="max-w-[280px] px-3 py-2 text-xs break-words">
                    {j.lastError ? (
                      <span className="text-danger">{j.lastError}</span>
                    ) : j.lastStatus ? (
                      `${j.lastStatus}${j.lastProcessed ? ` · ${j.lastProcessed} done` : ''}`
                    ) : (
                      '—'
                    )}
                    {j.setAside > 0 && feedOf(j.job) && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="mt-1"
                        onClick={() => setReviewing(feedOf(j.job))}
                        data-testid="review-set-aside"
                      >
                        Review {j.setAside}
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {reviewing && <SetAsideDialog feed={reviewing} onClose={() => setReviewing(null)} />}
    </Section>
  );
}

/**
 * The message feed's set-aside rows (TS-1892): Message Hub rows the CRM refused, by key and
 * reason (never their values). A corrected version in Message Hub resolves one by itself;
 * Dismiss is for the rest, so the feed stops reading failing.
 */
function SetAsideDialog({ feed, onClose }: { feed: 'texts' | 'emails'; onClose: () => void }) {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ['feed-rejections', feed],
    queryFn: () => api.system.feedRejections(feed),
  });
  const dismiss = useMutation({
    mutationFn: () => api.system.dismissFeedRejections(feed),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: QUERY });
      onClose();
    },
  });
  const rows = list.data?.items ?? [];
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Set-aside {feed}</DialogTitle>
          <DialogDescription>
            Message Hub rows the CRM could not store. Fixing one in Message Hub stores it on the
            next run. Dismiss the ones that need nothing more; the dismissal is in the audit log.
          </DialogDescription>
        </DialogHeader>
        {list.isError && (
          <p className="text-danger text-sm" role="alert">
            {describeError(list.error)}
          </p>
        )}
        {dismiss.isError && (
          <p className="text-danger text-sm" role="alert">
            {describeError(dismiss.error)}
          </p>
        )}
        <ul className="max-h-72 space-y-2 overflow-y-auto text-xs" data-testid="set-aside-rows">
          {rows.map((r) => (
            <li key={r.id} className="bg-muted rounded-lg px-3 py-2">
              <p className="font-mono break-all">{r.key ?? '(no key)'}</p>
              <p className="text-muted-foreground">
                {r.reasons.join(', ')} · {r.timesRejected}× · last {ago(r.lastRejectedAt)}
              </p>
            </li>
          ))}
        </ul>
        {list.data && list.data.total > rows.length && (
          <p className="text-muted-foreground text-xs">
            Showing the newest {rows.length} of {list.data.total}.
          </p>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
          <Button
            variant="destructive"
            onClick={() => dismiss.mutate()}
            disabled={dismiss.isPending || !list.data || list.data.total === 0}
            data-testid="dismiss-set-aside"
          >
            Dismiss all {list.data?.total ?? ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Sync({ sync }: { sync: SystemHealthView['sync'] }) {
  return (
    <Section
      icon={Mail}
      title="Mail and message sync"
      testId="sync"
      description="Emails and messages other systems post onto timelines (Relay's Gmail, Confirmly's WhatsApp), per source."
    >
      {sync.length === 0 ? (
        <p className="bg-muted text-muted-foreground rounded-xl px-4 py-3 text-sm">
          Nothing has been posted yet. Sources appear here after their first event.
        </p>
      ) : (
        <div className="bg-card border-border overflow-x-auto rounded-xl border">
          <table className="w-full min-w-[560px] text-left text-sm">
            <thead className="text-muted-foreground bg-muted text-xs whitespace-nowrap">
              <tr>
                <th className="px-3 py-2 font-semibold">Source</th>
                <th className="px-3 py-2 font-semibold">Last received</th>
                <th className="px-3 py-2 text-right font-semibold">Last 24h</th>
                <th className="px-3 py-2 text-right font-semibold">Unmatched</th>
              </tr>
            </thead>
            <tbody>
              {sync.map((s) => (
                <tr key={s.source} className="border-border border-t" data-testid="sync-source">
                  <td className="px-3 py-2 font-mono text-xs">{s.source}</td>
                  <td
                    className="px-3 py-2 text-xs whitespace-nowrap"
                    title={when(s.lastReceivedAt)}
                  >
                    {ago(s.lastReceivedAt)}
                  </td>
                  <td className="px-3 py-2 text-right text-xs tabular-nums">{s.received24h}</td>
                  <td
                    className={cn(
                      'px-3 py-2 text-right text-xs tabular-nums',
                      s.unmatched > 0 && 'text-state-warning-fg font-semibold',
                    )}
                  >
                    {s.unmatched > 0 ? (
                      <Link to="/settings/api-keys" className="underline-offset-2 hover:underline">
                        {s.unmatched}
                      </Link>
                    ) : (
                      0
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}

const DEFAULT = 'default';

function LogLevels({ levels }: { levels: LogLevelView[] }) {
  const qc = useQueryClient();
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: ({
      service,
      level,
    }: {
      service: LogLevelView['service'];
      level: RuntimeLogLevel | null;
    }) => api.system.setLogLevel(service, level),
    onSuccess: async (v) => {
      setError(null);
      setNotice(
        `${v.service}: ${v.override ?? 'back to its deploy-time level'}. Every instance picks it up within ${v.refreshSeconds}s; the change is in the audit log.`,
      );
      await qc.invalidateQueries({ queryKey: QUERY });
    },
    onError: (err) => setError(describeError(err)),
  });
  return (
    <Section
      icon={ScrollText}
      title="Log levels"
      testId="log-levels"
      description="Turn up logging on a service while you investigate, without a deploy. Remember to set it back."
    >
      {notice && (
        <p
          className="bg-state-success-bg text-state-success-fg border-state-success-border mb-3 rounded-xl border px-4 py-3 text-sm"
          role="status"
        >
          {notice}
        </p>
      )}
      {error && (
        <p className="text-danger mb-3 text-sm" role="alert">
          {error}
        </p>
      )}
      <Card className="divide-border divide-y">
        {levels.map((l) => (
          <div
            key={l.service}
            className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3 px-5 py-4"
            data-testid="log-level"
          >
            <div className="min-w-0">
              <p className="text-sm font-semibold">{l.service === 'api' ? 'API' : 'Worker'}</p>
              <p className="text-muted-foreground text-xs">
                {l.level !== null && `Running at ${l.level}`}
                {l.level !== null && l.configured !== null && ` (deployed at ${l.configured})`}
                {l.level === null &&
                  (l.override ? `Set to ${l.override}` : 'At its deploy-time level')}
                {l.updatedAt &&
                  ` · changed ${ago(l.updatedAt)}${l.updatedBy ? ` by ${l.updatedBy}` : ''}`}
              </p>
            </div>
            <select
              aria-label={`${l.service} log level`}
              value={l.override ?? DEFAULT}
              disabled={save.isPending}
              onChange={(e) =>
                save.mutate({
                  service: l.service,
                  level: e.target.value === DEFAULT ? null : (e.target.value as RuntimeLogLevel),
                })
              }
              className={cn(nativeSelectClass, 'h-9 sm:w-56')}
            >
              <option value={DEFAULT}>Deploy-time default</option>
              {LOG_LEVELS.map((level) => (
                <option key={level} value={level}>
                  {level.toUpperCase()}
                </option>
              ))}
            </select>
          </div>
        ))}
      </Card>
    </Section>
  );
}

function ms(n: number | null): string {
  if (n === null) return '—';
  return n < 1000 ? `${Math.round(n)} ms` : `${(n / 1000).toFixed(2)} s`;
}

function pct(rate: number): string {
  return rate === 0 ? '0%' : rate < 0.001 ? '<0.1%' : `${(rate * 100).toFixed(1)}%`;
}

function uptime(seconds: number): string {
  if (seconds < 3600) return `${Math.round(seconds / 60)} min`;
  if (seconds < 86_400) return `${Math.round(seconds / 3600)} h`;
  return `${Math.round(seconds / 86_400)} d`;
}

const ROUTES_SHOWN = 10;

function PerformanceSection() {
  const perf = useQuery({
    queryKey: ['performance'],
    queryFn: api.system.performance,
    refetchInterval: PERF_POLL_MS,
  });
  const [allRoutes, setAllRoutes] = useState(false);
  const p = perf.data;
  return (
    <Section
      icon={Gauge}
      title="Performance"
      testId="performance"
      description={
        p
          ? `The last ${p.windowMinutes} minutes, every API and worker instance together. Amber and red mark the lines the CloudWatch alarms page on.`
          : 'Request latency, worker loop durations and each instance’s memory and event-loop delay.'
      }
    >
      {perf.isPending && <p className="text-muted-foreground text-sm">Loading…</p>}
      {perf.isError && (
        <p className="text-danger text-sm" role="alert">
          {describeError(perf.error)}
        </p>
      )}
      {p && (
        <div className="flex flex-col gap-4">
          <dl className="grid grid-cols-2 gap-3 sm:grid-cols-5" data-testid="perf-totals">
            <Tile label="Requests / min" value={String(p.requests.perMinute)} />
            <Tile
              label="Errors (5xx)"
              value={pct(p.requests.errorRate)}
              tone={p.requests.errorRate > ERROR_RATE_ALARM ? 'danger' : undefined}
            />
            <Tile label="p50" value={ms(p.requests.p50Ms)} />
            <Tile
              label="p95"
              value={ms(p.requests.p95Ms)}
              tone={(p.requests.p95Ms ?? 0) > P95_ALARM_MS ? 'warning' : undefined}
            />
            <Tile label="p99" value={ms(p.requests.p99Ms)} />
          </dl>
          <RoutesTable
            routes={allRoutes ? p.routes : p.routes.slice(0, ROUTES_SHOWN)}
            total={p.routes.length}
            expanded={allRoutes}
            onToggle={() => setAllRoutes(!allRoutes)}
          />
          {p.jobs.length > 0 && <LoopsTable jobs={p.jobs} />}
          <InstancesTable instances={p.instances} />
        </div>
      )}
    </Section>
  );
}

function Tile({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'warning' | 'danger' | undefined;
}) {
  return (
    <div
      className={cn(
        'bg-card border-border rounded-xl border px-4 py-3',
        tone === 'warning' && 'border-state-warning-border bg-state-warning-bg',
        tone === 'danger' && 'border-state-danger-border bg-state-danger-bg',
      )}
    >
      <dt className="text-muted-foreground text-xs">{label}</dt>
      <dd
        className={cn(
          'mt-0.5 text-lg font-semibold tabular-nums',
          tone === 'warning' && 'text-state-warning-fg',
          tone === 'danger' && 'text-state-danger-fg',
        )}
      >
        {value}
      </dd>
    </div>
  );
}

function LatencyCells({
  row,
}: {
  row: {
    errorRate: number;
    p50Ms: number | null;
    p95Ms: number | null;
    p99Ms: number | null;
    maxMs: number;
  };
}) {
  return (
    <>
      <td
        className={cn(
          'px-3 py-2 text-right text-xs tabular-nums',
          row.errorRate > ERROR_RATE_ALARM && 'text-danger font-semibold',
        )}
      >
        {pct(row.errorRate)}
      </td>
      <td className="px-3 py-2 text-right text-xs tabular-nums">{ms(row.p50Ms)}</td>
      <td
        className={cn(
          'px-3 py-2 text-right text-xs tabular-nums',
          (row.p95Ms ?? 0) > P95_ALARM_MS && 'text-state-warning-fg font-semibold',
        )}
      >
        {ms(row.p95Ms)}
      </td>
      <td className="px-3 py-2 text-right text-xs tabular-nums">{ms(row.p99Ms)}</td>
      <td className="text-muted-foreground px-3 py-2 text-right text-xs tabular-nums">
        {ms(row.maxMs)}
      </td>
    </>
  );
}

const LATENCY_HEAD = ['Errors', 'p50', 'p95', 'p99', 'Max'];

function RoutesTable({
  routes,
  total,
  expanded,
  onToggle,
}: {
  routes: PerformanceView['routes'];
  total: number;
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <div>
      <h3 className="mb-2 text-sm font-semibold">Slowest routes</h3>
      {total === 0 ? (
        <p className="bg-muted text-muted-foreground rounded-xl px-4 py-3 text-sm">
          No requests in the window yet.
        </p>
      ) : (
        <div className="bg-card border-border overflow-x-auto rounded-xl border">
          <table className="w-full min-w-[720px] text-left text-sm">
            <thead className="text-muted-foreground bg-muted text-xs whitespace-nowrap">
              <tr>
                <th className="px-3 py-2 font-semibold">Route</th>
                <th className="px-3 py-2 text-right font-semibold">Requests</th>
                {LATENCY_HEAD.map((h) => (
                  <th key={h} className="px-3 py-2 text-right font-semibold">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {routes.map((r) => (
                <tr
                  key={`${r.method} ${r.route}`}
                  className="border-border border-t"
                  data-testid="perf-route"
                >
                  <td className="px-3 py-2">
                    <span className="flex items-center gap-2">
                      <Badge variant="outline" className="font-mono text-[10px]">
                        {r.method}
                      </Badge>
                      <span className="font-mono text-xs break-all">{r.route}</span>
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right text-xs tabular-nums">{r.count}</td>
                  <LatencyCells row={r} />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {total > ROUTES_SHOWN && (
        <Button size="sm" variant="ghost" className="mt-2" onClick={onToggle}>
          {expanded ? 'Show the slowest 10' : `Show all ${total} routes`}
        </Button>
      )}
    </div>
  );
}

function LoopsTable({ jobs }: { jobs: PerformanceView['jobs'] }) {
  return (
    <div>
      <h3 className="mb-2 text-sm font-semibold">Worker loop durations</h3>
      <div className="bg-card border-border overflow-x-auto rounded-xl border">
        <table className="w-full min-w-[640px] text-left text-sm">
          <thead className="text-muted-foreground bg-muted text-xs whitespace-nowrap">
            <tr>
              <th className="px-3 py-2 font-semibold">Loop</th>
              <th className="px-3 py-2 text-right font-semibold">Ticks</th>
              {LATENCY_HEAD.map((h) => (
                <th key={h} className="px-3 py-2 text-right font-semibold">
                  {h === 'Errors' ? 'Failed' : h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {jobs.map((j) => (
              <tr key={j.job} className="border-border border-t" data-testid="perf-loop">
                <td className="px-3 py-2 font-mono text-xs">{j.job}</td>
                <td className="px-3 py-2 text-right text-xs tabular-nums">{j.count}</td>
                <LatencyCells row={j} />
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function InstancesTable({ instances }: { instances: PerformanceView['instances'] }) {
  return (
    <div>
      <h3 className="mb-2 text-sm font-semibold">Instances</h3>
      {instances.length === 0 ? (
        <p className="bg-muted text-muted-foreground rounded-xl px-4 py-3 text-sm">
          No instance has reported in the last two minutes.
        </p>
      ) : (
        <div className="bg-card border-border overflow-x-auto rounded-xl border">
          <table className="w-full min-w-[720px] text-left text-sm">
            <thead className="text-muted-foreground bg-muted text-xs whitespace-nowrap">
              <tr>
                <th className="px-3 py-2 font-semibold">Instance</th>
                <th className="px-3 py-2 text-right font-semibold">Up</th>
                <th className="px-3 py-2 text-right font-semibold">Memory</th>
                <th className="px-3 py-2 text-right font-semibold">Heap</th>
                <th className="px-3 py-2 text-right font-semibold">Event-loop p99</th>
                <th className="px-3 py-2 text-right font-semibold">Reported</th>
              </tr>
            </thead>
            <tbody>
              {instances.map((i) => (
                <tr
                  key={i.instanceId}
                  className="border-border border-t"
                  data-testid="perf-instance"
                >
                  <td className="px-3 py-2">
                    <span className="flex items-center gap-2">
                      <Badge variant="secondary">{i.service}</Badge>
                      <span
                        className="text-muted-foreground max-w-[260px] truncate font-mono text-[11px]"
                        title={i.instanceId}
                      >
                        {i.instanceId}
                      </span>
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right text-xs tabular-nums">
                    {uptime(i.runtime.uptimeSeconds)}
                  </td>
                  <td className="px-3 py-2 text-right text-xs tabular-nums">
                    {Math.round(i.runtime.rssMb)} MB
                  </td>
                  <td className="px-3 py-2 text-right text-xs tabular-nums">
                    {Math.round(i.runtime.heapUsedMb)} / {Math.round(i.runtime.heapTotalMb)} MB
                  </td>
                  <td
                    className={cn(
                      'px-3 py-2 text-right text-xs tabular-nums',
                      i.runtime.eventLoopP99Ms > LOOP_DELAY_WARN_MS &&
                        'text-state-warning-fg font-semibold',
                    )}
                  >
                    {ms(i.runtime.eventLoopP99Ms)}
                  </td>
                  <td className="px-3 py-2 text-right text-xs whitespace-nowrap">
                    {ago(i.reportedAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
